// DjAudioEngine — main-thread TS surface for the wasm AudioWorklet engine.
//
// Loads the wasm module, adds the AudioWorklet processor, and drives the engine
// via the processor's `port`. Implements `IAudioEngine`, the shared contract
// consumed by H2000 and card-online.

import type { IAudioEngine, CueLogEntry } from './IAudioEngine';

export type { IAudioEngine, CueLogEntry };

export interface SampleSpec {
    /** Stable asset identity (preferred). */
    assetId?: string;
    /** Legacy input-array/project-track index; retained for H2000 compatibility. */
    track?: number;
    buffer: Float32Array;
    looping?: boolean;
    /** If true, this sample is a cue one-shot: it ignores transport gating and restarts on NoteOn. */
    oneShot?: boolean;
}

export interface SampleFileSpec {
    /** Stable asset identity (preferred). */
    assetId?: string;
    /** Legacy input-array/project-track index; retained for H2000 compatibility. */
    track?: number;
    bytes: Uint8Array;
    looping?: boolean;
    /** If true, this sample is a cue one-shot: it ignores transport gating and restarts on NoteOn. */
    oneShot?: boolean;
}

export interface SoundDesc {
    /** Oscillator type for tone sources. */
    osc?: 'sine' | 'square' | 'sawtooth' | 'triangle';
    /** Frequency in Hz — a single value, or [start, end] for a linear glide. */
    freq?: number | [number, number];
    /** Duration in milliseconds. */
    durationMs?: number;
    /** Linear gain (0..1). */
    gain?: number;
    /** Filter kind for noise sources. */
    filter?: 'lowpass' | 'highpass' | 'bandpass' | 'notch' | 'none';
    /** Filter frequency in Hz. */
    filterFreq?: number;
    /** Sample reference: track index of the pre-loaded sample. */
    sample?: { track: number };
}

export interface Cue {
    /** Unique cue identifier. */
    id: string;
    /** Target bus name (e.g., 'sfx', 'music'). */
    bus: string;
    /** Priority for voice stealing (0..1). */
    priority?: number;
    /** Sound definition for this cue. */
    sound: SoundDesc;
}

export interface CueTable {
    /** List of available cues. */
    cues: Cue[];
}

export interface DjAudioEngineOptions {
    /** Serialized `ProjectSnapshot` (JSON). */
    projectJson: string;
    /** Optional pre-resolved sample buffers (synth + sampled builds). */
    samples?: SampleSpec[];
    /** Optional encoded sample file bytes (requires `sampled` wasm build). */
    sampleFiles?: SampleFileSpec[];
    /** Optional cue table — enables `fire()` to send `Command::TriggerCue`. */
    cueTable?: CueTable;
}

/** Canonical project-bus order used by cue routing and `setBusGain`. */
export const WORKLET_BUS_INDEX = Object.freeze({
    music: 0,
    sfx: 1,
    ambient: 2,
    ui: 3,
    spatial: 4,
} as const);

export type WorkletBusName = keyof typeof WORKLET_BUS_INDEX;
export type WorkletBusIndex = (typeof WORKLET_BUS_INDEX)[WorkletBusName];

const OSC_TO_NUM: Record<string, number> = {
    sine: 0,
    square: 1,
    sawtooth: 2,
    triangle: 3,
};

const FILTER_TO_NUM: Record<string, number> = {
    lowpass: 0,
    highpass: 1,
    bandpass: 2,
    notch: 3,
    none: 255,
};

function clamp(v: number, min: number, max: number): number {
    return v < min ? min : v > max ? max : v;
}

// Exact serde shape of dj_audio_control::RealizedSound. Keep this internally
// tagged representation synchronized with the Rust wire; the older externally
// tagged `{ Sample: { track, gain } }` shape is rejected by current WASM.
type RealizedSound =
    | { kind: 'tone'; freq: number; osc: number; duration_ms: number; gain: number; sweep_to?: number }
    | { kind: 'noise'; duration_ms: number; gain: number; filter_kind: number; filter_freq: number }
    | { kind: 'sample'; sample: number; gain: number };

interface RealizedCue {
    bus: number;
    priority: number;
    /** Pan: -1..1 (left..right). */
    pan: number;
    /** Instance gain: 0..1. */
    gain: number;
    sound: RealizedSound;
}

/** JS/processor/WASM bundle protocol. Mixed-generation bundles fail closed. */
export const WORKLET_WIRE_VERSION = 1;
export const WORKLET_CAPABILITIES = Object.freeze({
    sampleCommands: true,
    sampledFiles: true,
});

export class DjAudioEngine implements IAudioEngine {
    private ctx: AudioContext;
    private node: AudioWorkletNode | null = null;
    private wasmModule: WebAssembly.Module | null = null;
    private pendingCueLog: CueLogEntry[] = [];
    private cueTable: Map<string, Cue> = new Map();
    private numCueBuses: number;
    private disposed = false;

    private constructor(ctx: AudioContext, opts: DjAudioEngineOptions) {
        this.ctx = ctx;
        for (const cue of opts.cueTable?.cues ?? []) {
            this.cueTable.set(cue.id, cue);
        }
        const project = JSON.parse(opts.projectJson) as { buses?: unknown[] };
        const numBuses = Math.max(1, project.buses?.length ?? 0);
        this.numCueBuses = this.cueTable.size > 0 ? numBuses : 0;
    }

    /** Create and boot a wasm-backed engine. */
    static async create(
        ctx: AudioContext,
        wasmUrl: string,
        processorUrl: string,
        opts: DjAudioEngineOptions
    ): Promise<DjAudioEngine> {
        const engine = new DjAudioEngine(ctx, opts);

        // Fetch+compile the wasm (worklet scope is sync-only, so we pass the
        // compiled Module) and add the processor module CONCURRENTLY — the two
        // awaits are independent; serializing them doubles boot latency.
        const [wasmModule] = await Promise.all([
            fetch(wasmUrl)
                .then((r) => r.arrayBuffer())
                .then((bytes) => WebAssembly.compile(bytes)),
            ctx.audioWorklet.addModule(processorUrl),
        ]);
        engine.wasmModule = wasmModule;

        // Build the AudioWorkletNode with everything the processor needs.
        const processorOptions: Record<string, unknown> = {
            wasmModule: engine.wasmModule,
            projectJson: opts.projectJson,
            numCueBuses: engine.numCueBuses,
        };
        if (opts.samples && opts.samples.length > 0) {
            processorOptions.samples = opts.samples;
        } else if (opts.sampleFiles && opts.sampleFiles.length > 0) {
            processorOptions.sampleFiles = opts.sampleFiles;
        }

        engine.node = new AudioWorkletNode(ctx, 'dj-audio-processor', {
            numberOfInputs: 0,
            numberOfOutputs: 1,
            outputChannelCount: [2],
            processorOptions,
        });

        // Wait for the processor's ready ack: the worklet constructor runs
        // initSync + the Rust build and can throw (LinkError, bad projectJson).
        // Without gating on ready/processorerror, a dead worklet would look
        // like a live engine to the consumer.
        let readyResolve: () => void;
        let readyReject: (reason?: unknown) => void;
        let readyTimer: ReturnType<typeof setTimeout>;
        const ready = new Promise<void>((res, rej) => {
            readyResolve = () => {
                clearTimeout(readyTimer);
                res();
            };
            readyReject = (reason) => {
                clearTimeout(readyTimer);
                rej(reason);
            };
            engine.node!.onprocessorerror = () =>
                readyReject(new Error('dj-audio-processor threw in the worklet scope'));
            readyTimer = setTimeout(
                () => readyReject(new Error('dj-audio-processor ready timeout (5s)')),
                5000
            );
        });

        engine.node.port.onmessage = (event) => {
            const { type, wireVersion, capabilities } = event.data || {};
            if (type === 'ready') {
                if (wireVersion !== WORKLET_WIRE_VERSION) {
                    readyReject(
                        new Error(
                            `dj-audio-worklet wire mismatch: wrapper=${WORKLET_WIRE_VERSION}, processor=${String(wireVersion)}`
                        )
                    );
                    return;
                }
                if (
                    capabilities?.sampleCommands !== WORKLET_CAPABILITIES.sampleCommands ||
                    capabilities?.sampledFiles !== WORKLET_CAPABILITIES.sampledFiles
                ) {
                    readyReject(
                        new Error('dj-audio-worklet capability mismatch for sampled commands')
                    );
                    return;
                }
                readyResolve();
            } else if (type === 'snapshot') {
                // Snapshot responses are not buffered in P1; real cue-log drain
                // arrives in P3.
            }
        };

        // Join the rendering graph — an unconnected worklet node renders to
        // nowhere (and the node is private, so no caller can connect it).
        engine.node.connect(ctx.destination);

        await ready;
        return engine;
    }

    async resume(): Promise<void> {
        if (this.disposed) return;
        await this.ctx.resume();
        // Unit variant in serde's externally-tagged format.
        this.postCommand('Play');
    }

    fire(cueId: string, params?: { pan?: number; gain?: number; emitterId?: string }): void {
        if (this.disposed) return;
        const cue = this.cueTable.get(cueId);
        if (!cue) {
            this.pendingCueLog.push({ cueId, time: this.ctx.currentTime });
            return;
        }
        const realized = this.realizeCue(cue, params);
        if (!realized) {
            this.pendingCueLog.push({ cueId, time: this.ctx.currentTime });
            return;
        }
        this.postCommand({ TriggerCue: { realized } });
        this.pendingCueLog.push({ cueId, time: this.ctx.currentTime });
    }

    private realizeCue(
        cue: Cue,
        params?: { pan?: number; gain?: number }
    ): RealizedCue | null {
        const busName = cue.bus.toLowerCase() as WorkletBusName;
        const busIdx = WORKLET_BUS_INDEX[busName];
        if (busIdx === undefined) return null;
        const sound = this.realizeSound(cue.sound);
        if (!sound) return null;
        const pan = clamp(params?.pan ?? 0, -1, 1);
        const gain = clamp(params?.gain ?? 1, 0, 1);
        return {
            bus: busIdx,
            priority: cue.priority ?? 0.5,
            pan,
            gain,
            sound,
        };
    }

    private realizeSound(desc: SoundDesc): RealizedSound | null {
        if (desc.sample) {
            return {
                kind: 'sample',
                sample: desc.sample.track,
                gain: desc.gain ?? 1.0,
            };
        }
        if (desc.freq !== undefined) {
            const osc = desc.osc ? OSC_TO_NUM[desc.osc] : 0;
            if (osc === undefined) return null;
            const [freq, sweepTo] = Array.isArray(desc.freq)
                ? desc.freq
                : [desc.freq, undefined];
            return {
                kind: 'tone',
                freq,
                osc,
                duration_ms: desc.durationMs ?? 200,
                gain: desc.gain ?? 0.2,
                ...(sweepTo !== undefined ? { sweep_to: sweepTo } : {}),
            };
        }
        const filter = desc.filter ? FILTER_TO_NUM[desc.filter] : 255;
        if (filter === undefined) return null;
        return {
            kind: 'noise',
            duration_ms: desc.durationMs ?? 200,
            gain: desc.gain ?? 0.2,
            filter_kind: filter,
            filter_freq: desc.filterFreq ?? 1000,
        };
    }

    setBusGain(bus: number, gain: number): void {
        if (this.disposed) return;
        // Struct variant in serde's externally-tagged format.
        this.postCommand({ SetBusGain: { bus, gain } });
    }

    setListener(_x: number, _y: number, _z: number): void {
        // P1: spatial listener is not yet wired. H2000 uses Option B (TS-side 2D).
    }

    drainCueLog(): CueLogEntry[] {
        const log = this.pendingCueLog;
        this.pendingCueLog = [];
        return log;
    }

    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.node?.disconnect();
        this.node?.port.close();
        this.node = null;
        this.wasmModule = null;
    }

    private postCommand(payload: Record<string, unknown> | string): void {
        this.node?.port.postMessage({ type: 'command', payload });
    }
}
